import type { MobType } from "@mmotdg/shared";
import * as THREE from "three";
import { createMobBodyGeometry, createMobBodyMaterial } from "./models/mobs/core.js";

interface PreviewEntry {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  scene: THREE.Scene;
  group: THREE.Group;
  body: THREE.Mesh;
  geometry: THREE.BufferGeometry;
  material: THREE.MeshStandardMaterial;
  type: MobType;
}

const PREVIEW_SIZE = 64;

class MobPreviewPool {
  private renderer: THREE.WebGLRenderer | null = null;
  private camera: THREE.OrthographicCamera | null = null;
  private offscreen: HTMLCanvasElement | null = null;
  private entries = new Map<HTMLCanvasElement, PreviewEntry>();
  private rafId = 0;
  private size = PREVIEW_SIZE;
  private startedAt = 0;

  private ensureRenderer(): void {
    if (this.renderer) return;
    this.offscreen = document.createElement("canvas");
    this.offscreen.width = this.size;
    this.offscreen.height = this.size;
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.offscreen,
      antialias: true,
      alpha: true,
      preserveDrawingBuffer: false,
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(this.size, this.size, false);
    this.renderer.setClearColor(0x000000, 0);

    const view = 0.55;
    this.camera = new THREE.OrthographicCamera(-view, view, view, -view, 0.1, 20);
    this.camera.position.set(1.8, 1.6, 1.8);
    this.camera.lookAt(0, 0.15, 0);
    this.startedAt = performance.now();
  }

  attach(canvas: HTMLCanvasElement, type: MobType): void {
    if (this.entries.has(canvas)) this.detach(canvas);
    this.ensureRenderer();
    canvas.width = this.size;
    canvas.height = this.size;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const scene = new THREE.Scene();
    scene.add(new THREE.AmbientLight(0xffffff, 0.65));
    const dir = new THREE.DirectionalLight(0xfff0d0, 1.1);
    dir.position.set(3, 5, 3);
    scene.add(dir);
    const hemi = new THREE.HemisphereLight(0x6ee7ff, 0x1c2333, 0.4);
    scene.add(hemi);

    const geometry = createMobBodyGeometry(type);
    const material = createMobBodyMaterial(type);
    const body = new THREE.Mesh(geometry, material);
    const group = new THREE.Group();
    group.add(body);
    scene.add(group);

    const entry: PreviewEntry = { canvas, ctx, scene, group, body, geometry, material, type };
    this.entries.set(canvas, entry);

    if (!this.rafId) this.start();
  }

  detach(canvas: HTMLCanvasElement): void {
    const entry = this.entries.get(canvas);
    if (!entry) return;
    entry.scene.remove(entry.group);
    entry.geometry.dispose();
    entry.material.dispose();
    this.entries.delete(canvas);
    if (this.entries.size === 0 && this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = 0;
    }
  }

  private start(): void {
    const loop = () => {
      this.rafId = requestAnimationFrame(loop);
      if (!this.renderer || !this.camera || !this.offscreen) return;
      const t = (performance.now() - this.startedAt) * 0.0009;
      for (const entry of this.entries.values()) {
        entry.group.rotation.y = t;
        this.renderer.render(entry.scene, this.camera);
        entry.ctx.clearRect(0, 0, this.size, this.size);
        entry.ctx.drawImage(this.offscreen, 0, 0);
      }
    };
    loop();
  }

  destroy(): void {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = 0;
    }
    for (const entry of this.entries.values()) {
      entry.scene.remove(entry.group);
      entry.geometry.dispose();
      entry.material.dispose();
    }
    this.entries.clear();
    this.renderer?.dispose();
    this.renderer = null;
    this.camera = null;
    this.offscreen = null;
  }
}

export const mobPreviewPool = new MobPreviewPool();
export { PREVIEW_SIZE as MOB_PREVIEW_SIZE };
